import React from 'react';

import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

import type { CreditCardForm, FormProps } from '../CreditCardForm.types';

export const useCreditCardTelemetry = ({
  isEditing,
  skipMountEvent,
  onSave,
  onEdit,
  onDelete,
}: UseCreditCardFormTelemetryProps) => {
  const { track } = useTelemetry();

  useTrackEventEffect({
    name: isEditing ? 'payment_credit.view_card' : 'payment_credit.add_card',
    skip: skipMountEvent,
  });

  const monitoredOnSave = React.useCallback(
    (data: CreditCardForm, nonce: string) => {
      if (data.default) track('payment_credit.set_default');
      track('payment_credit.save_card');
      onSave?.(data, nonce);
    },
    [track, onSave],
  );

  const monitoredOnEdit = React.useCallback(
    (data: CreditCardForm) => {
      track('payment_credit.save_card');
      onEdit?.(data);
    },
    [track, onEdit],
  );

  const monitoredOnFail = React.useCallback(
    (userError?: string, systemError?: string) => {
      track('payment_credit.save_card_failed', { userError, systemError });
    },
    [track],
  );

  const monitoredOnDelete = React.useCallback(() => {
    track('payment_credit.remove_card');
    onDelete?.();
  }, [track, onDelete]);

  return {
    monitoredOnSave,
    monitoredOnEdit,
    monitoredOnFail,
    monitoredOnDelete,
  };
};

type UseCreditCardFormTelemetryProps = {
  readonly skipMountEvent?: boolean;
} & Partial<Pick<FormProps, 'isEditing' | 'onSave' | 'onEdit' | 'onDelete'>>;
